import React, { useEffect, useRef, useState } from 'react';
import { AddCircle, Trash, Plus } from '../../assets/images';
import BasicModal from '../action-modal';
import { KeyValuePairProps } from './kay-value-pair';

export default function KeyValuePair({
  label,
  key,
  disableMode,
  initialKeyValue,
  field,
  isAddRemoveDynamicOptions,
  onChange,
  dynamicVariables,
}: KeyValuePairProps) {
  const [keyValue, setKeyValuePair] = useState(initialKeyValue);
  const [openPayloadModal, setOpenPayloadModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Create an array of refs for multiple input fields
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleAddKeyValuePair = () => {
    setKeyValuePair((prev) => [...prev, { key: '', value: '' }]);
    inputRefs.current.push(null); // Add a new ref for the new input
  };

  const deleteKeyValuePair = (deleteIndex: number) => {
    setKeyValuePair((prev) => prev?.filter((_, i) => i !== deleteIndex));
    inputRefs.current.splice(deleteIndex, 1); // Remove ref at index
  };

  useEffect(() => {
    onChange?.(initialKeyValue);
    setKeyValuePair(initialKeyValue);
  }, [initialKeyValue]);

  useEffect(() => {
    const result =
      keyValue?.length === 1 && keyValue[0]?.key === '' && keyValue[0]?.value === ''
        ? {}
        : keyValue?.reduce((acc: { [key: string]: string }, item) => {
            if (item.key !== '') {
              if (item.key !== undefined) {
                acc[item.key as string] = item.value as string;
              }
            }
            return acc;
          }, {});
    onChange?.(result);
  }, [keyValue]);

  const handleValueFromStorage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (selectedIndex === null) return;

    const input = inputRefs.current[selectedIndex];
    if (input) {
      const cursorPosition = input.selectionStart || 0;
      const currentValue = input.value;
      const newValue =
        currentValue.slice(0, cursorPosition) + value + currentValue.slice(cursorPosition);

      setKeyValuePair((prev) =>
        prev?.map((item, i) => (i === selectedIndex ? { ...item, value: newValue } : item)),
      );
    }
    setOpenPayloadModal(false);
  };

  const handleCancelClick = () => {
    setOpenPayloadModal(false);
  };

  return (
    <div key={`field-${key}`}>
      <div className="flex justify-between items-center">
        <span className="text-[#344054] text-sm font-medium">{label}</span>
        {isAddRemoveDynamicOptions && (
          <button
            type="button"
            className={`cursor-pointer ${disableMode && 'pointer-events-none'}`}
            onClick={handleAddKeyValuePair}
          >
            <img src={AddCircle} alt="Add Variable" className="transform  w-[15px] h-[15px]" />
          </button>
        )}
      </div>

      {field?.props?.isWithMessage && keyValue?.length === 0 && (
        <p className="text-sm text-[#828282] mb-4">
          You are good to test this assistant. Hit continue.
        </p>
      )}
      {keyValue?.map((pair, index) => (
        <div key={`${pair.key}`} className="flex gap-2 mb-2 w-full items-center">
          <div className="w-1/2">
            {field?.props?.withKeyLabels && (
              <span className="text-[#344054] text-sm font-medium">Key</span>
            )}
            <input
              placeholder="Key"
              className="mt-1 text-sm py-2 border rounded-lg w-full"
              value={pair.key}
              disabled={disableMode}
              onChange={(e) => {
                const { value } = e.target;
                setKeyValuePair((prev) => {
                  const newPairs = [...prev];
                  newPairs[index].key = value;
                  return newPairs;
                });
              }}
            />
          </div>
          <div className="w-1/2">
            {field?.props?.withKeyLabels && (
              <span className="text-[#344054] text-sm font-medium">Value</span>
            )}
            <div className="relative">
              <input
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                data-key={`fields_dynamic-${index}`}
                placeholder="Value"
                className="mt-1 text-sm py-2 border rounded-lg w-full"
                value={pair.value}
                disabled={disableMode}
                onChange={(e) => {
                  const { value } = e.target;
                  setKeyValuePair((prev) => {
                    const newPairs = [...prev];
                    newPairs[index].value = value;
                    return newPairs;
                  });
                }}
              />
              <button
                type="button"
                onClick={() => {
                  setSelectedIndex(index);
                  setOpenPayloadModal(true);
                }}
                className={` cursor-pointer ${disableMode && 'pointer-events-none'}`}
              >
                <img
                  src={Plus}
                  alt="Add Variable"
                  className={`absolute right-2 top-1/2 transform -translate-y-1/2 w-[15px] h-[15px] `}
                />
              </button>
            </div>
          </div>
          {isAddRemoveDynamicOptions && keyValue?.length > 1 && (
            <button
              type="button"
              className={`hover:cursor-pointer ${disableMode && 'pointer-events-none'}`}
              onClick={() => deleteKeyValuePair(index)}
            >
              <img src={Trash} className={` w-[15px] h-[15px] `} alt="Remove Variable" />
            </button>
          )}
        </div>
      ))}
      <BasicModal
        className="w-[400px]"
        open={openPayloadModal}
        confirmButtonText="Continue"
        resetData={handleCancelClick}
        saveData={handleCancelClick}
      >
        <ul>
          {(dynamicVariables?.length ?? 0) > 0 ? (
            dynamicVariables?.map((item: string) => (
              <li className="flex gap-2 items-center" key={item}>
                <input type="radio" name="keys" value={item} onChange={handleValueFromStorage} />
                {item}
              </li>
            ))
          ) : (
            <span>No keys found!</span>
          )}
        </ul>
      </BasicModal>
    </div>
  );
}
